import { Trash2 } from 'lucide-react';
import { InfoTooltip } from '../../components/ui/InfoTooltip';
import {
  CONTEXT_SOURCES,
  CONTEXT_SOURCE_ORDER,
  isListInputType,
  type ContextSource,
  type InputLocation,
  type ToolInputRow,
  type ToolInputType,
} from './tool-inputs';
import styles from './ToolForm.module.css';

const INPUT_TYPES: ToolInputType[] = [
  'string',
  'number',
  'boolean',
  'string[]',
  'number[]',
  'boolean[]',
];

const LIST_TYPE_HINT = 'Lists can only be sent in the request body';

interface ToolInputRowEditorProps {
  row: ToolInputRow;
  issue?: string;
  /** GET has no body, so the location picker is locked to the query string. */
  locationLocked: boolean;
  /** The row's name matches a `{{placeholder}}` in the path template, so it fills the path. */
  fillsPath: boolean;
  onUpdate: (patch: Partial<ToolInputRow>) => void;
  onRemove: () => void;
}

export const ToolInputRowEditor = ({
  row,
  issue,
  locationLocked,
  fillsPath,
  onUpdate,
  onRemove,
}: ToolInputRowEditorProps) => {
  // A Reach-sourced field is auto-supplied at request time, so it's auto-required (always present).
  const setSource = (value: string) =>
    onUpdate(value ? { source: value as ContextSource, required: true } : { source: undefined });

  const ridesInQuery = locationLocked || row.location === 'query';
  const listTypesBlocked = fillsPath || ridesInQuery;
  const requiredLocked = Boolean(row.source) || fillsPath;
  const pathRequiredHint = `This input fills {{${row.name}}} in the path — the URL can't be built without it, so it can't be made optional.`;
  const requiredLockHint = row.source
    ? CONTEXT_SOURCES[row.source].requiredHint
    : fillsPath
      ? pathRequiredHint
      : null;
  const pathLocationHint = `The path contains {{${row.name}}}, so this input fills that placeholder instead of riding in the ${ridesInQuery ? 'query string' : 'body'}. Rename the input or edit the path to change that.`;
  const rowLabel = row.name || 'parameter';

  return (
    <tr>
      <td>
        <input
          type="text"
          value={row.name}
          onChange={e => onUpdate({ name: e.target.value })}
          className={styles.paramNameInput}
          placeholder="phone"
          aria-invalid={Boolean(issue)}
        />
        {issue && <span className={styles.paramError}>{issue}</span>}
      </td>
      <td>
        <select
          value={row.type}
          onChange={e => onUpdate({ type: e.target.value as ToolInputType })}
          className={styles.paramTypeSelect}
          aria-label={`${rowLabel} type`}>
          {INPUT_TYPES.map(type => {
            const blocked = isListInputType(type) && listTypesBlocked;
            return (
              <option
                key={type}
                value={type}
                disabled={blocked}
                title={blocked ? LIST_TYPE_HINT : undefined}>
                {type}
              </option>
            );
          })}
        </select>
      </td>
      <td className={styles.paramRequiredCell}>
        <input
          type="checkbox"
          checked={requiredLocked ? true : row.required}
          disabled={requiredLocked}
          onChange={e => onUpdate({ required: e.target.checked })}
          className={styles.checkbox}
          aria-label={`${rowLabel} required`}
        />
        {requiredLockHint && (
          <InfoTooltip label="Why is this always required?">
            <p>{requiredLockHint}</p>
          </InfoTooltip>
        )}
      </td>
      <td>
        <select
          value={row.source ?? ''}
          onChange={e => setSource(e.target.value)}
          className={styles.paramSourceSelect}
          disabled={fillsPath}
          aria-label={`${rowLabel} source`}>
          <option value="">From the conversation</option>
          <optgroup label="Filled by Reach">
            {CONTEXT_SOURCE_ORDER.map(source => (
              <option key={source} value={source}>
                {CONTEXT_SOURCES[source].label}
              </option>
            ))}
          </optgroup>
        </select>
      </td>
      {fillsPath ? (
        <td className={styles.paramLocationCell}>
          <select
            value="path"
            disabled
            className={styles.paramTypeSelect}
            aria-label={`${rowLabel} location`}>
            <option value="path">Path</option>
          </select>
          <InfoTooltip label="Why Path?">
            <p>{pathLocationHint}</p>
          </InfoTooltip>
        </td>
      ) : (
        <td>
          <select
            value={locationLocked ? 'query' : (row.location ?? 'body')}
            onChange={e => onUpdate({ location: e.target.value as InputLocation })}
            className={styles.paramTypeSelect}
            disabled={locationLocked}
            aria-label={`${rowLabel} location`}>
            <option value="body">Body</option>
            <option value="query">Query</option>
          </select>
        </td>
      )}
      <td>
        <input
          type="text"
          value={row.description ?? ''}
          onChange={e => onUpdate({ description: e.target.value })}
          className={styles.paramDescriptionInput}
          placeholder={
            row.source
              ? 'Optional — Reach fills this in; the agent never sees it'
              : 'What the agent should put here'
          }
        />
      </td>
      <td>
        <button
          type="button"
          onClick={onRemove}
          className={styles.removeButton}
          aria-label={`Remove ${rowLabel}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};
